import {
  JsonToBoolean,
  JsonToDouble,
  JsonToInt32,
  JsonToInt64,
  JsonToString,
  JsonToList,
  ArrayToJson,
  JsonToMap,
  MapToJson,
} from '../Helper/Converter';
class LocalizationEntry_Translation {
  get descriptor() {
    return LocalizationEntry_Translation.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasText = false;
    this._text = null;
    this._hasOrigin = false;
    this._origin = null;
  }
  isEmpty() {
    if (this._hasText) return false;
    if (this._hasOrigin) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof LocalizationEntry_Translation) {
      return value;
    }
    return LocalizationEntry_Translation.fromJson(value);
  }
  static fromJson(json) {
    const result = new LocalizationEntry_Translation();
    if (json.text != null) {
      result._hasText = true;
      result._text = JsonToString.fromJson(json.text);
    }
    if (json.origin != null) {
      result._hasOrigin = true;
      result._origin = JsonToString.fromJson(json.origin);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasText) {
      result.text = JsonToString.toJson(this._text);
    }
    if (this._hasOrigin) {
      result.origin = JsonToString.toJson(this._origin);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get text() {
    return this._hasText ? this._text : JsonToString.default;
  }
  get hasText() {
    return this._hasText;
  }
  setText(path, value) {
    return {type: 'SET', path: path.concat(['text']), value};
  }
  deleteText(path) {
    return {type: 'DELETE', path: path.concat(['text'])};
  }
  /**
   * @returns {string}
   */
  get origin() {
    return this._hasOrigin ? this._origin : JsonToString.default;
  }
  get hasOrigin() {
    return this._hasOrigin;
  }
  setOrigin(path, value) {
    return {type: 'SET', path: path.concat(['origin']), value};
  }
  deleteOrigin(path) {
    return {type: 'DELETE', path: path.concat(['origin'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "text": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new LocalizationEntry_Translation(this);
              copy._hasText = true;
              copy._text = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasText) {
                return this;
              }
              const copy = new LocalizationEntry_Translation(this);
              copy._hasText = false;
              copy._text = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "origin": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new LocalizationEntry_Translation(this);
              copy._hasOrigin = true;
              copy._origin = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasOrigin) {
                return this;
              }
              const copy = new LocalizationEntry_Translation(this);
              copy._hasOrigin = false;
              copy._origin = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {LocalizationEntry_Translation} other
   * @returns {LocalizationEntry_Translation}
   */
  merge(other) {
    const copy = new LocalizationEntry_Translation(this);
    if (other._hasText) {
      copy._hasText = true;
      copy._text = other._text;
    }
    if (other._hasOrigin) {
      copy._hasOrigin = true;
      copy._origin = other._origin;
    }
    return copy;
  }
}
LocalizationEntry_Translation.default = new LocalizationEntry_Translation();
class LocalizationEntry_Translation_Builder {
  constructor(value) {
    this._value = new LocalizationEntry_Translation(value);
  }
  build() {
    return new LocalizationEntry_Translation(this._value);
  }
  setText(value) {
    this._value._hasText = true;
    this._value._text = value;
    return this;
  }
  deleteText() {
    this._value._hasText = false;
    this._value._text = null;
    return this;
  }
  setOrigin(value) {
    this._value._hasOrigin = true;
    this._value._origin = value;
    return this;
  }
  deleteOrigin() {
    this._value._hasOrigin = false;
    this._value._origin = null;
    return this;
  }
}
LocalizationEntry_Translation.Builder = LocalizationEntry_Translation_Builder;
class LocalizationEntry_Translation_WithParent {
  /**
   * @param {LocalizationEntry_Translation} value
   * @param {LocalizationEntry_Translation_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return LocalizationEntry_Translation.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasText() {
    if (this._cachedHasText === undefined) {
      if (this._value.hasText) {
        this._cachedHasText = true;
      } else {
        this._cachedHasText = this._parent ? this._parent.hasText : false;
      }
    }
    return this._cachedHasText;
  }
  get text() {
    if (this._cachedText === undefined) {
      if (this._value.hasText) {
        this._cachedText = this._value.text;
      } else {
        this._cachedText = this._parent ? this._parent.text : this._value.text;
      }
    }
    return this._cachedText;
  }
  get hasOrigin() {
    if (this._cachedHasOrigin === undefined) {
      if (this._value.hasOrigin) {
        this._cachedHasOrigin = true;
      } else {
        this._cachedHasOrigin = this._parent ? this._parent.hasOrigin : false;
      }
    }
    return this._cachedHasOrigin;
  }
  get origin() {
    if (this._cachedOrigin === undefined) {
      if (this._value.hasOrigin) {
        this._cachedOrigin = this._value.origin;
      } else {
        this._cachedOrigin = this._parent ? this._parent.origin : this._value.origin;
      }
    }
    return this._cachedOrigin;
  }
  update(current, parent) {
    const result = new LocalizationEntry_Translation_WithParent(current, parent);
    return result;
  }
}
LocalizationEntry_Translation_WithParent.default = new LocalizationEntry_Translation_WithParent(LocalizationEntry_Translation.default, null);
LocalizationEntry_Translation.WithParent = LocalizationEntry_Translation_WithParent;
class LocalizationEntry_Translation_Descriptor {
  get text() {
    if (!this._text) {
      this._text = {
        name: "text",
        Name: "Text",
        number: 1,
        protoType: 9,
        protoName: "text",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._text;
  }
  get origin() {
    if (!this._origin) {
      this._origin = {
        name: "origin",
        Name: "Origin",
        number: 2,
        protoType: 9,
        protoName: "origin",
        type: JsonToString,
        options: {
          comments: "The original English text where this translation is made from. This helps us detect changes in English so we know when to update.",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._origin;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.text,
        this.origin,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForText",
        Name: "DummyOneofForText",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForOrigin",
        Name: "DummyOneofForOrigin",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
LocalizationEntry_Translation.descriptor = new LocalizationEntry_Translation_Descriptor();
class LocalizationEntry_TranslationsEntry {
  get descriptor() {
    return LocalizationEntry_TranslationsEntry.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasKey = false;
    this._key = null;
    this._hasValue = false;
    this._value = null;
  }
  isEmpty() {
    if (this._hasKey) return false;
    if (this._hasValue && !this._value.isEmpty()) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof LocalizationEntry_TranslationsEntry) {
      return value;
    }
    return LocalizationEntry_TranslationsEntry.fromJson(value);
  }
  static fromJson(json) {
    const result = new LocalizationEntry_TranslationsEntry();
    if (json.key != null) {
      result._hasKey = true;
      result._key = JsonToString.fromJson(json.key);
    }
    if (json.value != null) {
      result._hasValue = true;
      result._value = LocalizationEntry_Translation.fromJson(json.value);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasKey) {
      result.key = JsonToString.toJson(this._key);
    }
    if (this._hasValue && !this._value.isEmpty()) {
      result.value = LocalizationEntry_Translation.toJson(this._value);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get key() {
    return this._hasKey ? this._key : JsonToString.default;
  }
  get hasKey() {
    return this._hasKey;
  }
  setKey(path, value) {
    return {type: 'SET', path: path.concat(['key']), value};
  }
  deleteKey(path) {
    return {type: 'DELETE', path: path.concat(['key'])};
  }
  /**
   * @returns {LocalizationEntry_Translation}
   */
  get value() {
    return this._hasValue ? this._value : LocalizationEntry_Translation.default;
  }
  get hasValue() {
    return this._hasValue;
  }
  setValue(path, value) {
    return {type: 'SET', path: path.concat(['value']), value: value.toJson()};
  }
  deleteValue(path) {
    return {type: 'DELETE', path: path.concat(['value'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "key": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new LocalizationEntry_TranslationsEntry(this);
              copy._hasKey = true;
              copy._key = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasKey) {
                return this;
              }
              const copy = new LocalizationEntry_TranslationsEntry(this);
              copy._hasKey = false;
              copy._key = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "value": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new LocalizationEntry_TranslationsEntry(this);
              copy._hasValue = true;
              copy._value = LocalizationEntry_Translation.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasValue) {
                return this;
              }
              const copy = new LocalizationEntry_TranslationsEntry(this);
              copy._hasValue = false;
              copy._value = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (!this._hasValue) {
            const copy = new LocalizationEntry_TranslationsEntry(this);
            copy._hasValue = true;
            copy._value = LocalizationEntry_Translation.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this._value.apply(type, path.slice(1), value);
          if (newValue === this._value) {
            return this;
          }
          const copy = new LocalizationEntry_TranslationsEntry(this);
          copy._value = copy._value.apply(type, path.slice(1), value);
          return copy;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {LocalizationEntry_TranslationsEntry} other
   * @returns {LocalizationEntry_TranslationsEntry}
   */
  merge(other) {
    const copy = new LocalizationEntry_TranslationsEntry(this);
    if (other._hasKey) {
      copy._hasKey = true;
      copy._key = other._key;
    }
    if (other._hasValue) {
      if (copy._hasValue) {
        copy._value = copy.value.merge(other.value);
      } else {
        copy._value = other._value;
      }
      copy._hasValue = true;
    }
    return copy;
  }
}
LocalizationEntry_TranslationsEntry.default = new LocalizationEntry_TranslationsEntry();
class LocalizationEntry_TranslationsEntry_Builder {
  constructor(value) {
    this._value = new LocalizationEntry_TranslationsEntry(value);
  }
  build() {
    return new LocalizationEntry_TranslationsEntry(this._value);
  }
  setKey(value) {
    this._value._hasKey = true;
    this._value._key = value;
    return this;
  }
  deleteKey() {
    this._value._hasKey = false;
    this._value._key = null;
    return this;
  }
  setValue(value) {
    this._value._hasValue = true;
    this._value._value = value;
    return this;
  }
  deleteValue() {
    this._value._hasValue = false;
    this._value._value = null;
    return this;
  }
}
LocalizationEntry_TranslationsEntry.Builder = LocalizationEntry_TranslationsEntry_Builder;
class LocalizationEntry_TranslationsEntry_WithParent {
  /**
   * @param {LocalizationEntry_TranslationsEntry} value
   * @param {LocalizationEntry_TranslationsEntry_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return LocalizationEntry_TranslationsEntry.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasKey() {
    if (this._cachedHasKey === undefined) {
      if (this._value.hasKey) {
        this._cachedHasKey = true;
      } else {
        this._cachedHasKey = this._parent ? this._parent.hasKey : false;
      }
    }
    return this._cachedHasKey;
  }
  get key() {
    if (this._cachedKey === undefined) {
      if (this._value.hasKey) {
        this._cachedKey = this._value.key;
      } else {
        this._cachedKey = this._parent ? this._parent.key : this._value.key;
      }
    }
    return this._cachedKey;
  }
  get hasValue() {
    if (this._cachedHasValue === undefined) {
      if (this._value.hasValue) {
        this._cachedHasValue = true;
      } else {
        this._cachedHasValue = this._parent ? this._parent.hasValue : false;
      }
    }
    return this._cachedHasValue;
  }
  get value() {
    if (!this._cachedValue) {
      if (this._value.hasValue) {
        this._cachedValue = new LocalizationEntry_Translation.WithParent(this._value.value, this._parent && this._parent.hasValue ? this._parent.value : null);
      } else {
        this._cachedValue = this._parent ? new LocalizationEntry_Translation.WithParent(LocalizationEntry_Translation.default, this._parent.value) : LocalizationEntry_Translation.WithParent.default;
      }
    }
    return this._cachedValue;
  }
  update(current, parent) {
    const result = new LocalizationEntry_TranslationsEntry_WithParent(current, parent);
    if (this._cachedValue && this._value.hasValue && current.hasValue) {
      if (this._value.value === current.value && this._parent === parent) {
        result._cachedValue = this._cachedValue;
      } else {
        result._cachedValue = this._cachedValue.update(current.value, parent && parent.hasValue ? parent.value : null);
      }
    }
    return result;
  }
}
LocalizationEntry_TranslationsEntry_WithParent.default = new LocalizationEntry_TranslationsEntry_WithParent(LocalizationEntry_TranslationsEntry.default, null);
LocalizationEntry_TranslationsEntry.WithParent = LocalizationEntry_TranslationsEntry_WithParent;
class LocalizationEntry_TranslationsEntry_Descriptor {
  get key() {
    if (!this._key) {
      this._key = {
        name: "key",
        Name: "Key",
        number: 1,
        protoType: 9,
        protoName: "key",
        type: JsonToString,
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._key;
  }
  get value() {
    if (!this._value) {
      this._value = {
        name: "value",
        Name: "Value",
        number: 2,
        protoType: 11,
        protoName: "value",
        type: LocalizationEntry_Translation,
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._value;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.key,
        this.value,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
LocalizationEntry_TranslationsEntry.descriptor = new LocalizationEntry_TranslationsEntry_Descriptor();
export class LocalizationEntry {
  get descriptor() {
    return LocalizationEntry.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasText = false;
    this._text = null;
    this._hasDescription = false;
    this._description = null;
    this._translations = {};
  }
  isEmpty() {
    if (this._hasText) return false;
    if (this._hasDescription) return false;
    if (Object.keys(this._translations).length > 0) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof LocalizationEntry) {
      return value;
    }
    return LocalizationEntry.fromJson(value);
  }
  static fromJson(json) {
    const result = new LocalizationEntry();
    if (json.text != null) {
      result._hasText = true;
      result._text = JsonToString.fromJson(json.text);
    }
    if (json.description != null) {
      result._hasDescription = true;
      result._description = JsonToString.fromJson(json.description);
    }
    result._translations = JsonToMap(json.translations, JsonToString, LocalizationEntry_Translation);
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasText) {
      result.text = JsonToString.toJson(this._text);
    }
    if (this._hasDescription) {
      result.description = JsonToString.toJson(this._description);
    }
    if (Object.keys(this._translations).length > 0) {
      result.translations = MapToJson(this._translations, JsonToString, LocalizationEntry_Translation);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get text() {
    return this._hasText ? this._text : JsonToString.default;
  }
  get hasText() {
    return this._hasText;
  }
  setText(path, value) {
    return {type: 'SET', path: path.concat(['text']), value};
  }
  deleteText(path) {
    return {type: 'DELETE', path: path.concat(['text'])};
  }
  /**
   * @returns {string}
   */
  get description() {
    return this._hasDescription ? this._description : JsonToString.default;
  }
  get hasDescription() {
    return this._hasDescription;
  }
  setDescription(path, value) {
    return {type: 'SET', path: path.concat(['description']), value};
  }
  deleteDescription(path) {
    return {type: 'DELETE', path: path.concat(['description'])};
  }
  /**
   * @returns {Object<string, LocalizationEntry_Translation>}
   */
  get translations() {
    return this._translations;
  }
  setTranslations(path, value) {
    return {type: 'SET', path: path.concat(['translations']), value: MapToJson(value, JsonToString, LocalizationEntry_Translation)};
  }
  setTranslationsByKey(path, key, value) {
    return {type: 'SET', path: path.concat(['translations', key]), value: value.toJson()};
  }
  deleteTranslations(path) {
    return {type: 'DELETE', path: path.concat(['translations'])};
  }
  deleteTranslationsByKey(path, key) {
    return {type: 'DELETE', path: path.concat(['translations', key])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "text": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new LocalizationEntry(this);
              copy._hasText = true;
              copy._text = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasText) {
                return this;
              }
              const copy = new LocalizationEntry(this);
              copy._hasText = false;
              copy._text = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "description": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new LocalizationEntry(this);
              copy._hasDescription = true;
              copy._description = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasDescription) {
                return this;
              }
              const copy = new LocalizationEntry(this);
              copy._hasDescription = false;
              copy._description = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "translations": {
        if (path.length === 1) {
          switch (type) {
            case 'DELETE': {
              if (Object.keys(this._translations).length === 0) {
                return this;
              }
              const copy = new LocalizationEntry(this);
              copy._translations = {};
              return copy;
            }
            case 'SET': {
              const copy = new LocalizationEntry(this);
              copy._translations = {};
              for (let key in value) {
                copy._translations[key] = LocalizationEntry_Translation.fromAny(value[key]);
              }
              return copy;
            }
            default:
              return this;
          }
        } else if (path.length === 2) {
          switch (type) {
            case 'SET': {
              const copy = new LocalizationEntry(this);
              copy._translations = {...copy._translations};
              copy._translations[path[1]] = LocalizationEntry_Translation.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._translations[path[1]]) {
                return this;
              }
              const copy = new LocalizationEntry(this);
              copy._translations = {...copy._translations};
              delete copy._translations[path[1]];
              return copy;
            }
            default:
              return this;
          }
        } else {
          const key = path[1];
          if (this._translations[key] === undefined) {
            const copy = new LocalizationEntry(this);
            copy._translations = {...copy._translations};
            copy._translations[key] = LocalizationEntry_Translation.default.apply(type, path.slice(2), value);
            return copy;
          }
          const oldValue = this._translations[key];
          const newValue = oldValue.apply(type, path.slice(2), value);
          if (oldValue === newValue) {
            return this;
          }
          const copy = new LocalizationEntry(this);
          copy._translations = {...copy._translations};
          copy._translations[key] = newValue;
          return copy;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {LocalizationEntry} other
   * @returns {LocalizationEntry}
   */
  merge(other) {
    const copy = new LocalizationEntry(this);
    if (other._hasText) {
      copy._hasText = true;
      copy._text = other._text;
    }
    if (other._hasDescription) {
      copy._hasDescription = true;
      copy._description = other._description;
    }
    copy._translations = {...this._translations, ...other._translations};
    return copy;
  }
}
LocalizationEntry.default = new LocalizationEntry();
LocalizationEntry.Translation = LocalizationEntry_Translation;
LocalizationEntry.TranslationsEntry = LocalizationEntry_TranslationsEntry;
class LocalizationEntry_Builder {
  constructor(value) {
    this._value = new LocalizationEntry(value);
  }
  build() {
    return new LocalizationEntry(this._value);
  }
  setText(value) {
    this._value._hasText = true;
    this._value._text = value;
    return this;
  }
  deleteText() {
    this._value._hasText = false;
    this._value._text = null;
    return this;
  }
  setDescription(value) {
    this._value._hasDescription = true;
    this._value._description = value;
    return this;
  }
  deleteDescription() {
    this._value._hasDescription = false;
    this._value._description = null;
    return this;
  }
  setTranslations(key, value) {
    this._value._translations = {...this._value._translations, [key]: value};
    return this;
  }
  addAllTranslations(values) {
    this._value._translations = {...this._value._translations, ...values};
    return this;
  }
  deleteTranslations(key) {
    this._value._translations = {...this._value._translations};
    delete this._value._translations[key];
    return this;
  }
  clearTranslations() {
    this._value._translations = {};
    return this;
  }
}
LocalizationEntry.Builder = LocalizationEntry_Builder;
class LocalizationEntry_WithParent {
  /**
   * @param {LocalizationEntry} value
   * @param {LocalizationEntry_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return LocalizationEntry.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasText() {
    if (this._cachedHasText === undefined) {
      if (this._value.hasText) {
        this._cachedHasText = true;
      } else {
        this._cachedHasText = this._parent ? this._parent.hasText : false;
      }
    }
    return this._cachedHasText;
  }
  get text() {
    if (this._cachedText === undefined) {
      if (this._value.hasText) {
        this._cachedText = this._value.text;
      } else {
        this._cachedText = this._parent ? this._parent.text : this._value.text;
      }
    }
    return this._cachedText;
  }
  get hasDescription() {
    if (this._cachedHasDescription === undefined) {
      if (this._value.hasDescription) {
        this._cachedHasDescription = true;
      } else {
        this._cachedHasDescription = this._parent ? this._parent.hasDescription : false;
      }
    }
    return this._cachedHasDescription;
  }
  get description() {
    if (this._cachedDescription === undefined) {
      if (this._value.hasDescription) {
        this._cachedDescription = this._value.description;
      } else {
        this._cachedDescription = this._parent ? this._parent.description : this._value.description;
      }
    }
    return this._cachedDescription;
  }
  get selfTranslations() {
    if (!this._selfTranslations) {
      this._selfTranslations = {};
      for (let i in this._value.translations) {
        this._selfTranslations[i] = new LocalizationEntry_Translation.WithParent(this._value.translations[i], null);
      }
    }
    return this._selfTranslations;
  }
  get inherentTranslations() {
    if (!this._inherentTranslations) {
      this._inherentTranslations = this._parent ? this._parent.translations : {};
    }
    return this._inherentTranslations;
  }
  get translations() {
    if (!this._allTranslations) {
      this._allTranslations = this._parent ? {...this._parent.translations, ...this.selfTranslations} : this.selfTranslations;
    }
    return this._allTranslations;
  }
  update(current, parent) {
    const result = new LocalizationEntry_WithParent(current, parent);
    if (this._selfTranslations) {
      if (this._value.translations === current.translations) {
        result._selfTranslations = this._selfTranslations;
      } else {
        result._selfTranslations = {};
        for (let i in current.translations) {
          const oldValue = this._value.translations[i];
          if (oldValue) {
            const oldCache = this._selfTranslations[i];
            if (oldValue === current.translations[i]) {
              result._selfTranslations[i] = oldCache;
            } else {
              result._selfTranslations[i] = oldCache.update(current.translations[i], null);
            }
          } else {
            result._selfTranslations[i] = new LocalizationEntry_Translation.WithParent(current.translations[i], null);
          }
        }
      }
    }
    return result;
  }
}
LocalizationEntry_WithParent.default = new LocalizationEntry_WithParent(LocalizationEntry.default, null);
LocalizationEntry.WithParent = LocalizationEntry_WithParent;
class LocalizationEntry_Descriptor {
  get text() {
    if (!this._text) {
      this._text = {
        name: "text",
        Name: "Text",
        number: 1,
        protoType: 9,
        protoName: "text",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._text;
  }
  get description() {
    if (!this._description) {
      this._description = {
        name: "description",
        Name: "Description",
        number: 2,
        protoType: 9,
        protoName: "description",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._description;
  }
  get translations() {
    if (!this._translations) {
      this._translations = {
        name: "translations",
        Name: "Translations",
        number: 3,
        protoType: 11,
        protoName: "translations",
        isMap: true,
        keyType: JsonToString,
        valueType: LocalizationEntry_Translation,
        keyProtoType: 9,
        valueProtoType: 11,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._translations;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.text,
        this.description,
        this.translations,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForText",
        Name: "DummyOneofForText",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForDescription",
        Name: "DummyOneofForDescription",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
LocalizationEntry.descriptor = new LocalizationEntry_Descriptor();
